/**
 * Активность помощников — «что у меня сейчас считается» на всём сайте.
 *
 * Жалоба владельца 22.09: запустил документ у помощника, перешёл к соседнему —
 * статус пропал. Причина в самом разделе: спиннер выводился из ОТКРЫТОГО
 * запуска (`busyAgentId` из стейта `run`), а смена помощника этот стейт
 * обнуляет; уход из раздела размонтирует страницу целиком.
 *
 * Источник правды здесь — сервер: у запуска есть статус и время конца, а
 * `/assistants/runs/active` отдаёт идущие и законченные за час. Поэтому, в
 * отличие от медиа-стикеров (`generationStickers.ts`), снимок в браузере не
 * нужен: входы лежат в самом запуске, deep-link `/assistants/<слуг>?run=…`
 * открывает его как есть. Стор только опрашивает список и помнит, какие
 * готовые человек уже посмотрел (sessionStorage — как у медиа-стикеров:
 * вчерашняя вкладка — не наша забота).
 *
 * Опрос: 5 с пока что-то считается, минута в покое, 5 минут если раздел
 * закрыт (404) или человек не вошёл (401); по возврату во вкладку — сразу.
 */
import { useEffect, useState } from 'react'

import { ACTIVE_STATUSES, getActiveRuns, type ActiveRun } from './assistants'
import { showToast } from '../components/Toast'

export const BUSY_MS = 5_000
export const IDLE_MS = 60_000
export const DISABLED_MS = 5 * 60_000
const SEEN_KEY = 'umnik.assistant_runs_seen'
const CHANGED = 'umnik:assistant-activity-changed'

export type ActivityStatus = 'running' | 'done' | 'error'

export interface ActivityItem {
	id: string
	status: ActivityStatus
	/** Подпись стикера: название работы, без него — помощник. */
	title: string
	iconUrl: string | null
	/** Куда вернуть по клику. */
	path: string
	error?: string | null
}

// ── чистые правила (покрыты тестами) ─────────────────────────

export const isBusyStatus = (s: string): boolean => (ACTIVE_STATUSES as string[]).includes(s)
export const isFinishedStatus = (s: string): boolean => s === 'done' || s === 'failed' || s === 'cancelled'

export function busyAgentIds(list: Pick<ActiveRun, 'status' | 'agent_id'>[]): string[] {
	const out: string[] = []
	for (const r of list) if (isBusyStatus(r.status) && r.agent_id && !out.includes(r.agent_id)) out.push(r.agent_id)
	return out
}

export function busyRunIds(list: Pick<ActiveRun, 'status' | 'id'>[]): string[] {
	return list.filter((r) => isBusyStatus(r.status)).map((r) => r.id)
}

/** Адрес запуска: посадочная помощника + ?run= (слуг есть у всех живых помощников;
 *  без него — корень раздела, он тоже открывает ?run=, как ссылка из колокольчика). */
export function runPath(r: Pick<ActiveRun, 'id' | 'agent_slug'>): string {
	return r.agent_slug ? `/assistants/${r.agent_slug}?run=${r.id}` : `/assistants?run=${r.id}`
}

/**
 * Стикеры из списка: идущие — всегда; законченные — пока человек их не посмотрел.
 * `clarifying` (помощник ждёт ответа) стикером не показываем — это отдельная
 * история (вопрос виден в самом разделе), здесь только «считается / готово».
 */
export function stickerItems(list: ActiveRun[], seen: ReadonlySet<string>): ActivityItem[] {
	const out: ActivityItem[] = []
	for (const r of list) {
		const title = r.title || r.solution_title || r.agent_title || 'Помощник'
		const base = { id: r.id, title, iconUrl: r.agent_icon_url ?? null, path: runPath(r) }
		if (isBusyStatus(r.status)) out.push({ ...base, status: 'running' })
		else if (isFinishedStatus(r.status) && !seen.has(r.id)) {
			out.push(r.status === 'done' ? { ...base, status: 'done' } : { ...base, status: 'error', error: r.error_message })
		}
	}
	return out
}

/** Что закончилось между двумя опросами: было занято — стало готово/ошибка. */
export function finishedSince(prev: Pick<ActiveRun, 'id' | 'status'>[], next: ActiveRun[]): ActiveRun[] {
	const wasBusy = new Set(prev.filter((r) => isBusyStatus(r.status)).map((r) => r.id))
	return next.filter((r) => wasBusy.has(r.id) && isFinishedStatus(r.status))
}

export function nextDelay(list: Pick<ActiveRun, 'status'>[]): number {
	return list.some((r) => isBusyStatus(r.status)) ? BUSY_MS : IDLE_MS
}

// ── «посмотрел» ───────────────────────────────────────────────

function readSeen(): Set<string> {
	try {
		const raw = sessionStorage.getItem(SEEN_KEY)
		const list = raw ? (JSON.parse(raw) as unknown) : []
		return new Set(Array.isArray(list) ? (list as string[]) : [])
	} catch {
		return new Set()
	}
}

export function markSeen(id: string): void {
	const seen = readSeen()
	seen.add(id)
	try {
		sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-100)))
	} catch {
		/* quota — стикер вернётся после перезагрузки, не страшно */
	}
	window.dispatchEvent(new CustomEvent(CHANGED))
}

// ── стор и опрос ──────────────────────────────────────────────

let runs: ActiveRun[] = []
let timer: number | null = null
let started = false
let inFlight: Promise<void> | null = null
let openRunId: string | null = null

export function activeRuns(): ActiveRun[] {
	return runs
}

/** Открытый в разделе запуск: о его завершении раздел скажет сам, тост не дублируем. */
export function setOpenAssistantRun(id: string | null): void {
	openRunId = id
}

function schedule(ms: number): void {
	if (timer !== null) window.clearTimeout(timer)
	timer = window.setTimeout(() => void tick(), ms)
}

function announce(finished: ActiveRun[]): void {
	for (const r of finished) {
		if (r.id === openRunId) continue
		const name = r.title || r.solution_title || 'Документ'
		if (r.status === 'done') showToast(`«${name}» готов — откройте по стикеру`, 'success')
		else if (r.status === 'failed') showToast(`«${name}»: ${r.error_message || 'ошибка генерации'}`, 'error')
	}
}

async function tick(): Promise<void> {
	if (inFlight) return inFlight
	inFlight = (async () => {
		if (!localStorage.getItem('token')) {
			if (runs.length) {
				runs = []
				window.dispatchEvent(new CustomEvent(CHANGED))
			}
			schedule(IDLE_MS)
			return
		}
		try {
			const r = await getActiveRuns()
			const next = r.runs ?? []
			announce(finishedSince(runs, next))
			runs = next
			window.dispatchEvent(new CustomEvent(CHANGED))
			schedule(nextDelay(next))
		} catch (e) {
			const status = (e as { status?: number }).status
			schedule(status === 401 || status === 403 || status === 404 ? DISABLED_MS : IDLE_MS)
		}
	})().finally(() => {
		inFlight = null
	})
	return inFlight
}

function ensureStarted(): void {
	if (started) return
	started = true
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden) void tick()
	})
	void tick()
}

/** Немедленный опрос: раздел зовёт его, когда запуск создан или сменил статус. */
export function refreshAssistantActivity(): void {
	if (!started) {
		ensureStarted()
		return
	}
	void tick()
}

export function useAssistantActivity(): ActiveRun[] {
	const [list, setList] = useState<ActiveRun[]>(runs)
	useEffect(() => {
		ensureStarted()
		const reload = () => setList(runs)
		window.addEventListener(CHANGED, reload)
		reload()
		return () => window.removeEventListener(CHANGED, reload)
	}, [])
	return list
}

/** Стикеры активности: пересчитываются и по опросу, и по отметке «посмотрел». */
export function useActivityItems(): ActivityItem[] {
	const [items, setItems] = useState<ActivityItem[]>(() => stickerItems(runs, readSeen()))
	useEffect(() => {
		ensureStarted()
		const reload = () => setItems(stickerItems(runs, readSeen()))
		window.addEventListener(CHANGED, reload)
		reload()
		return () => window.removeEventListener(CHANGED, reload)
	}, [])
	return items
}
